import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../app/AppShell';
import { useSession } from '../app/SessionProvider';
import { useArchiveThread, useCreateThread, usePatchThread, useThreads } from '../api/hooks';
import { IconChevron, IconPlus } from '../components/icons';
import { Badge, Button, Card, EmptyState, ErrorText, Spinner } from '../components/ui';
import { zh } from '../i18n';
import { formatAggregateCost } from '../lib/cost';
import { formatRelative } from '../lib/format';

/**
 * 對話列表 (SPEC §10): cost chips, a pending-approval dot, and a 新對話 FAB.
 *
 * The cost chip goes through `formatAggregateCost`, so under a subscription
 * credential the list shows 「訂閱額度制」 instead of a column of `$0.00` — the
 * same honesty rule as the per-turn line (CONTRACTS §4.1).
 *
 * Archiving is a soft archive (CONTRACTS §4.3 `DELETE` = archive): no volume
 * data and no SDK session file is ever removed, so the toggle is safe to offer
 * inline rather than behind a destructive-action confirmation.
 */
export function ThreadListRoute(): JSX.Element {
  const [archived, setArchived] = useState(false);
  const threads = useThreads(archived);
  const createThread = useCreateThread();
  const archiveThread = useArchiveThread();
  const patchThread = usePatchThread();
  const navigate = useNavigate();
  const { budgetsEnforced } = useSession();

  const items = threads.data?.pages.flatMap((page) => page.items) ?? [];

  const startThread = (): void => {
    createThread.mutate({}, { onSuccess: (thread) => navigate(`/c/${thread.id}`) });
  };

  return (
    <AppShell
      trailing={
        <Button variant="ghost" small onClick={() => setArchived((value) => !value)}>
          {archived ? zh.threads.hideArchived : zh.threads.showArchived}
        </Button>
      }
    >
      {/*
        SPEC §10's two panes. The list is the subject of this screen, so it
        takes the wider half and the second pane holds the placeholder a
        conversation will replace. `.panes` is `display: contents` below 1024 px
        — a phone shows the list and nothing else.
      */}
      <div className="panes panes--list">
        <div className="panes__list">
          <div className="page-head">
            <h1 className="page-head__title">{zh.threads.title}</h1>
            {archived ? <Badge tone="muted">{zh.threads.archived}</Badge> : null}
          </div>

          {threads.isLoading ? <Spinner /> : null}
          <ErrorText error={threads.error} />

          {threads.isSuccess && items.length === 0 ? (
            <EmptyState
              title={zh.threads.emptyTitle}
              body={zh.threads.emptyBody}
              action={
                <Button variant="primary" onClick={startThread} disabled={createThread.isPending}>
                  {createThread.isPending ? zh.threads.creating : zh.threads.newThread}
                </Button>
              }
            />
          ) : null}

          {items.length > 0 ? (
            <Card flush>
              <ul className="list">
                {items.map((item) => (
                  <li key={item.id} className="list__item">
                    <button
                      type="button"
                      className="list__row"
                      onClick={() => navigate(`/c/${item.id}`)}
                    >
                      <div className="list__main">
                        <div className="list__title">{item.title || zh.threads.untitled}</div>
                        <div className="list__sub">
                          {item.preview || zh.threads.messageCount(item.messageCount)}
                        </div>
                      </div>
                      <div className="stack stack--sm" style={{ alignItems: 'flex-end' }}>
                        {item.hasPendingApproval ? (
                          <Badge tone="primary">{zh.threads.pendingApproval}</Badge>
                        ) : null}
                        <span className="tiny faint nowrap">
                          {item.lastTurnAt === null
                            ? zh.threads.messageCount(item.messageCount)
                            : zh.threads.lastActive(formatRelative(item.lastTurnAt))}
                        </span>
                        <span className="tiny faint nowrap">
                          {item.totalCostUsd > 0 || budgetsEnforced
                            ? formatAggregateCost(item.totalCostUsd, budgetsEnforced)
                            : zh.threads.noCostYet}
                        </span>
                      </div>
                      <IconChevron size={16} className="tool__icon" />
                    </button>
                    <Button
                      variant="ghost"
                      small
                      disabled={archiveThread.isPending || patchThread.isPending}
                      onClick={() => {
                        if (item.archived) patchThread.mutate({ id: item.id, archived: false });
                        else archiveThread.mutate(item.id);
                      }}
                    >
                      {item.archived ? zh.threads.unarchive : zh.threads.archive}
                    </Button>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          {threads.hasNextPage ? (
            <Button
              variant="ghost"
              block
              disabled={threads.isFetchingNextPage}
              onClick={() => void threads.fetchNextPage()}
            >
              {threads.isFetchingNextPage ? zh.app.loading : zh.threads.loadMore}
            </Button>
          ) : null}
        </div>

        <div className="panes__detail">
          <EmptyState
            title={zh.threads.detailPlaceholderTitle}
            body={zh.threads.detailPlaceholderBody}
          />
        </div>
      </div>

      <button type="button" className="fab" onClick={startThread} disabled={createThread.isPending}>
        <IconPlus size={18} /> {zh.threads.newThread}
      </button>
    </AppShell>
  );
}
